import {
  conflictError,
  storageError,
  type AppError,
} from '@obraplan/core/application/errors/app-error';
import { ERROR_CODE, messageForCode } from '@obraplan/core/application/errors/error-codes';
import type { Clock } from '@obraplan/core/application/ports/clock';
import type { Session } from '@obraplan/core/application/session';
import { err, ok, type Result } from '@obraplan/core/domain/shared/result';
import {
  runConferenceOperation,
  semResposta,
  type ConferenceUseCases,
} from '@/app/conference-operations';
import type { ConferenceOperation, Outbox, OutboxEntry, SubmitOutcome } from '@/app/outbox-types';
import type { OutboxRow } from '@/infrastructure/persistence/db';
import type { OutboxStore } from '@/infrastructure/persistence/outbox-store';
import { ESGOTADO, noPrazo } from '@/lib/no-prazo';

/**
 * Os cabeçalhos do passo que vem do aparelho. Gêmeos de `server/api/device-operation.ts`: o
 * id do passo (RN-606) e a hora em que a pessoa o fez (RN-607).
 */
export const OPERATION_HEADER = 'x-obraplan-operacao';
export const DEVICE_TIME_HEADER = 'x-obraplan-feito-em';

export interface OutboxDeps {
  readonly store: OutboxStore;
  readonly clock: Clock;
  readonly newId: () => string;
  /**
   * Os casos de uso de **uma tentativa** de uma operação: cada chamada leva `<id>#<n>`, com o
   * `n` contado desde zero. Uma tentativa nova começa do zero de novo, e os passos que já tinham
   * gravado recebem do servidor a mesma resposta, sem gravar outra vez.
   */
  readonly useCasesFor: (entry: {
    readonly id: string;
    readonly createdAt: string;
  }) => ConferenceUseCases;
  /**
   * Se vale tentar a rede: o `navigator.onLine` e a última resposta do servidor. Sem rede, ou com
   * a API sem responder há pouco, nem se tenta — a operação vai direto para a fila.
   */
  readonly isOnline: () => boolean;
  /**
   * Quanto a tela espera a gravação na hora (RN-619). Estourado, ela segue pela fila, e a tentativa
   * continua sozinha: no sinal fraco a rede não cai e não chega, e a tela não pode ficar presa.
   */
  readonly waitMs?: number;
  /**
   * Quanto uma tentativa pode ficar sem resposta antes de a fila desistir dela e deixá-la para a
   * próxima volta, com o mesmo id (RN-606). Sem isso, a rede que nunca responde prenderia a fila.
   */
  readonly abandonAfterMs?: number;
  /** Avisa as telas de que a fila mudou. */
  readonly onChange: () => void;
}

/**
 * A chave da ordem: a fila mantém a ordem dentro dela, e o recusado segura o que vem depois.
 *
 * Para a pessoa, a equipe e o RDO, é o dia (obra × data × chave). Para a frente, a data é o dia
 * escolhido no apontamento, e duas marcas na mesma frente com dias diferentes também precisam
 * esperar uma pela outra: a chave é só a frente.
 */
function diaDe(operation: ConferenceOperation): string {
  return operation.kind === 'comodos' || operation.kind === 'pecas-da-frente'
    ? `${operation.projectId}|${operation.resourceId}`
    : `${operation.projectId}|${operation.date}|${operation.resourceId}`;
}

function paraEntrada(row: OutboxRow): OutboxEntry {
  return {
    id: row.id,
    userId: row.userId,
    createdAt: row.createdAt,
    operation: row.operation as ConferenceOperation,
    state: row.state,
    attempts: row.attempts,
    ...(row.error === undefined ? {} : { error: row.error }),
  };
}

/** Quanto a tela espera a gravação na hora, sem `waitMs`. */
export const WAIT_FOR_NETWORK_MS = 5000;

/** Quanto uma tentativa pode ficar sem resposta, sem `abandonAfterMs`. */
export const ABANDON_AFTER_MS = 60_000;

/** A sessão que caiu: a fila espera a pessoa entrar de novo, e então sobe. */
function sessaoRecusada(error: AppError): boolean {
  return error.code === ERROR_CODE.SESSION_EXPIRED || error.code === ERROR_CODE.SESSION_REQUIRED;
}

/**
 * A fila da Conferência no aparelho (Fase 43, etapa 2, RN-603 a RN-610).
 *
 * - **Guarda primeiro, e grava na hora quando dá** (`submit`, RN-619). A operação entra no
 *   aparelho antes da primeira tentativa: fechar ou recarregar com a rede pendurada não a perde.
 *   A tela espera a resposta por `waitMs`; depois disso, segue pela fila, e a tentativa termina
 *   sozinha. Se o servidor não responder no meio, a próxima tentativa vai com o mesmo id e não
 *   grava duas vezes o que já tinha gravado (RN-606).
 * - **Sobe na ordem em que foi feita** (`flush`), um registro por vez, e só com a sessão de
 *   quem fez (RN-605, RN-610).
 * - **O recusado fica**, com o motivo do servidor, e segura os seguintes do mesmo dia da mesma
 *   pessoa: subir uma confirmação depois de uma ausência recusada contaria outra história.
 */
export function createOutbox(deps: OutboxDeps): Outbox {
  let subindo = false;
  /** A operação que está subindo agora: o arquivo dela não sai da fila no meio (RN-622). */
  let emVoo: string | undefined;
  /** As que o `submit` tenta agora: a volta da fila não as sobe junto, e o dia delas espera. */
  const correndo = new Set<string>();
  const prazoDaTela = deps.waitMs ?? WAIT_FOR_NETWORK_MS;
  const desistirEm = deps.abandonAfterMs ?? ABANDON_AFTER_MS;
  let deNovo: Session | undefined;
  let ultimaSeq = 0;

  const proximaSeq = (): number => {
    ultimaSeq = Math.max(ultimaSeq + 1, deps.clock.now().getTime() * 1000);
    return ultimaSeq;
  };

  /**
   * A tentativa na hora de uma operação que já está no aparelho. Enquanto a tela espera, a recusa
   * volta para ela e a operação sai da fila; depois do prazo, a recusa fica no cartão (RN-608), e
   * a falta de resposta deixa a operação para a próxima volta.
   */
  const tentarAgora = async (
    row: OutboxRow,
    session: Session,
    telaEsperando: () => boolean,
  ): Promise<Result<SubmitOutcome, AppError>> => {
    correndo.add(row.id);
    let feita: Awaited<ReturnType<typeof runConferenceOperation>> | typeof ESGOTADO;
    try {
      feita = await noPrazo(
        runConferenceOperation(
          row.operation as ConferenceOperation,
          deps.useCasesFor(row),
          session,
        ),
        desistirEm,
      );
    } finally {
      correndo.delete(row.id);
    }
    // A rede não respondeu nem no teto: a próxima volta tenta de novo, com o mesmo id.
    if (feita === ESGOTADO) {
      if ((await deps.store.get(row.id).catch(() => undefined)) !== undefined) {
        await deps.store.put({ ...row, attempts: row.attempts + 1 }).catch(() => undefined);
      }
      if (!telaEsperando()) deps.onChange();
      return ok({ queued: true });
    }
    const resposta = await guardarODesfecho(row, feita, telaEsperando()).catch(
      (): Result<SubmitOutcome, AppError> => ok({ queued: true }),
    );
    // O que entrou atrás desta, no mesmo dia, esperava por ela. Sem resposta, a próxima volta é a
    // do relógio: tentar de novo agora só martelaria a rede que não responde.
    if (feita.ok && deps.isOnline()) void outbox.flush(session);
    return resposta;
  };

  const guardarODesfecho = async (
    row: OutboxRow,
    feita: Awaited<ReturnType<typeof runConferenceOperation>>,
    telaEsperando: boolean,
  ): Promise<Result<SubmitOutcome, AppError>> => {
    // Descartada enquanto subia: não volta para a fila.
    const aindaLa = (await deps.store.get(row.id)) !== undefined;
    if (feita.ok) {
      if (aindaLa) await deps.store.delete(row.id);
      if (!telaEsperando) deps.onChange();
      return ok({ queued: false, outcome: feita.value });
    }
    if (semResposta(feita.error) || (!telaEsperando && sessaoRecusada(feita.error))) {
      if (aindaLa) await deps.store.put({ ...row, attempts: row.attempts + 1 });
      if (!telaEsperando) deps.onChange();
      return ok({ queued: true });
    }
    if (telaEsperando) {
      if (aindaLa) await deps.store.delete(row.id);
      return feita;
    }
    if (aindaLa) {
      await deps.store.put({
        ...row,
        state: 'rejected',
        error: { code: feita.error.code, message: feita.error.message },
        attempts: row.attempts + 1,
      });
    }
    deps.onChange();
    return ok({ queued: true });
  };

  const outbox: Outbox = {
    async submit(operation, session): Promise<Result<SubmitOutcome, AppError>> {
      const row: OutboxRow = {
        id: deps.newId(),
        userId: session.userId,
        seq: proximaSeq(),
        createdAt: deps.clock.nowIso(),
        operation,
        state: 'pending',
        attempts: 0,
      };

      // O que já espera neste dia vai antes: esta operação entra atrás, para não passar na frente.
      const fila = await deps.store.all(session.userId).catch(() => []);
      const esperando = fila.some(
        (outra) => diaDe(outra.operation as ConferenceOperation) === diaDe(operation),
      );
      // Primeiro no aparelho (RN-619), sem avisar a tela: com sinal, sai daqui em um instante.
      try {
        await deps.store.put(row);
      } catch {
        // O aparelho sem espaço (RN-609): melhor dizer agora do que perder calado.
        return err(
          storageError(ERROR_CODE.QUOTA_EXCEEDED, messageForCode(ERROR_CODE.QUOTA_EXCEEDED)),
        );
      }
      if (esperando || !deps.isOnline()) {
        deps.onChange();
        if (deps.isOnline()) void outbox.flush(session);
        return ok({ queued: true });
      }

      let telaEsperando = true;
      const feita = await noPrazo(
        tentarAgora(row, session, () => telaEsperando),
        prazoDaTela,
      );
      if (feita !== ESGOTADO) return feita;
      telaEsperando = false;
      deps.onChange();
      return ok({ queued: true });
    },

    async list(userId, filter) {
      const linhas = await deps.store.all(userId).catch(() => []);
      return linhas
        .map(paraEntrada)
        .filter(
          (entrada) =>
            (filter?.projectId === undefined || entrada.operation.projectId === filter.projectId) &&
            (filter?.date === undefined || entrada.operation.date === filter.date),
        );
    },

    async flush(session) {
      // Já está subindo: este pedido roda logo depois da volta atual, e não no próximo relógio —
      // a volta atual leu a fila antes, e não vê o que acabou de entrar (o "Tentar de novo").
      if (subindo) {
        deNovo = session;
        return 0;
      }
      subindo = true;
      let subiram = 0;
      let mudou = false;
      try {
        const fila = await deps.store.all(session.userId);
        const travados = new Set<string>();
        for (const row of fila) {
          const dia = diaDe(row.operation as ConferenceOperation);
          if (row.state === 'rejected') {
            travados.add(dia);
            continue;
          }
          if (travados.has(dia)) continue;
          // O `submit` ainda tenta esta: ela e o resto do dia esperam a próxima volta.
          if (correndo.has(row.id)) {
            travados.add(dia);
            continue;
          }

          emVoo = row.id;
          const feita = await noPrazo(
            runConferenceOperation(
              row.operation as ConferenceOperation,
              deps.useCasesFor(row),
              session,
            ),
            desistirEm,
          );
          emVoo = undefined;
          // A rede pendurada não prende a fila: esta e o resto esperam a próxima volta (RN-619).
          if (feita === ESGOTADO) {
            await deps.store.put({ ...row, attempts: row.attempts + 1 });
            break;
          }
          if (feita.ok) {
            await deps.store.delete(row.id);
            subiram += 1;
            mudou = true;
            continue;
          }
          // A tentativa conta mesmo sem recusa: os passos do começo podem ter gravado, e a foto
          // de uma operação já tentada não sai mais da fila pela posição (RN-622).
          const tentada = { ...row, attempts: row.attempts + 1 };
          // Ainda sem sinal, ou a sessão caiu: o resto espera a próxima volta, na ordem.
          if (semResposta(feita.error) || sessaoRecusada(feita.error)) {
            await deps.store.put(tentada);
            break;
          }
          // Outra aba está subindo o mesmo passo: este dia fica para a próxima volta.
          if (feita.error.code === ERROR_CODE.DEVICE_OPERATION_BUSY) {
            await deps.store.put(tentada);
            travados.add(dia);
            continue;
          }
          await deps.store.put({
            ...row,
            state: 'rejected',
            error: { code: feita.error.code, message: feita.error.message },
            attempts: row.attempts + 1,
          });
          travados.add(dia);
          mudou = true;
        }
      } catch {
        // O banco do aparelho falhou no meio: o que não subiu continua lá para a próxima volta.
      } finally {
        subindo = false;
        emVoo = undefined;
      }
      if (mudou) deps.onChange();
      const pedida = deNovo;
      deNovo = undefined;
      return pedida === undefined ? subiram : subiram + (await outbox.flush(pedida));
    },

    async retry(id, session) {
      const row = await deps.store.get(id);
      if (row === undefined) return;
      const { error: _motivo, ...semMotivo } = row;
      await deps.store.put({ ...semMotivo, state: 'pending' });
      deps.onChange();
      await outbox.flush(session);
    },

    async discard(id) {
      await deps.store.delete(id);
      deps.onChange();
    },

    async dropFile(id, index) {
      const row = await deps.store.get(id);
      if (row === undefined) return ok(undefined);
      const operation = row.operation as ConferenceOperation;
      if (!('files' in operation) || index < 0 || index >= operation.files.length) {
        return ok(undefined);
      }
      // Cada arquivo é um passo, contado pela posição (RN-606): tirar um do meio de uma operação
      // que já subiu em parte faria o seguinte receber a resposta do que saiu, e não subir.
      if (row.attempts > 0 || emVoo === id || correndo.has(id)) {
        return err(
          conflictError(
            ERROR_CODE.DEVICE_OPERATION_BUSY,
            'Esta foto já começou a subir. Remova-a pela galeria quando o sinal voltar.',
          ),
        );
      }
      const files = operation.files.filter((_file, posicao) => posicao !== index);
      try {
        if (files.length === 0) await deps.store.delete(id);
        else await deps.store.put({ ...row, operation: { ...operation, files } });
      } catch {
        return err(
          storageError(ERROR_CODE.QUOTA_EXCEEDED, messageForCode(ERROR_CODE.QUOTA_EXCEEDED)),
        );
      }
      deps.onChange();
      return ok(undefined);
    },
  };

  return outbox;
}
